import React, { useCallback, useEffect, useRef, useState } from 'react';

import { loadScript } from '../../util/util';

import './StoryAdSlide.scss';

const IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
// A no-fill or a dead ad server must never trap the viewer on the slot.
const AD_START_TIMEOUT_MS = 5000;
// Separate, and much shorter: this one only covers getting the SDK itself. An ad blocker that
// answers with an error or a stub is caught at once, but one that simply never answers leaves both
// onload and onerror unfired, and waiting the full five seconds for that is five seconds of black
// card. If google.ima is not there by now it is not coming.
const SDK_READY_TIMEOUT_MS = 2000;

interface StoryAdSlideProps {
	adTagUrl: string;
	isActive: boolean;
	/** The break actually started playing. Not fired for a no-fill or a blocked SDK. */
	onStarted?: () => void;
	/** The viewer left the break early, rather than it playing out. */
	onSkipped?: () => void;
	/** Called on completion, error, skip or timeout - always exactly once. */
	onFinished: () => void;
	/**
	 * Called once when it becomes known that no creative is coming. `isOnScreen` is whether the
	 * viewer is standing on the slot: one they have not reached can be dropped from the feed there
	 * and then, one they are looking at has to be scrolled away from first or the feed snaps.
	 */
	onNoFill?: (isOnScreen: boolean) => void;
	/** 0..1 the ad plays at when it is not muted. */
	startVolume?: number;
	/** Audio is one setting for the whole feed. Undefined leaves the slot its own state. */
	muted?: boolean;
	/** Reports the viewer's use of the mute toggle, so the rest of the feed can follow it. */
	onMutedChange?: (_muted: boolean) => void;
	/** Seconds before the slide offers its own skip. 0 makes the ad unskippable. */
	skipAfterSeconds?: number;
	label?: string;
	/** Shown on the placeholder card when the ad server returns nothing. */
	noAdLabel?: string;
	muteLabel?: string;
	unmuteLabel?: string;
	playLabel?: string;
	pauseLabel?: string;
	skipLabel?: string;
	/** Countdown text. `{{seconds}}` is replaced as it ticks. */
	skipCountdownLabel?: string;
	/**
	 * The language IMA draws its OWN chrome in - skip button, countdown, "Learn more". Left unset it
	 * follows the browser, so a Danish feed read by someone whose browser is English says "Skip Ad"
	 * beside text that says everything else in Danish. Nothing to do with the creative, which is
	 * whatever the ad server trafficked.
	 */
	locale?: string;
}

/**
 * A standalone ad slot. GoBrain's Advertisement module is videojs-ima bound to a content player, so
 * it cannot fill a slide with no video; this talks to the IMA SDK directly.
 */
const StoryAdSlide = ({
	adTagUrl,
	isActive,
	onFinished,
	onStarted,
	onSkipped,
	onNoFill,
	startVolume = 0,
	muted,
	onMutedChange,
	skipAfterSeconds = 5,
	label,
	noAdLabel,
	muteLabel,
	unmuteLabel,
	playLabel,
	pauseLabel,
	skipLabel,
	skipCountdownLabel,
	locale,
}: StoryAdSlideProps) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const adsLoaderRef = useRef<any>(null);
	const adsManagerRef = useRef<any>(null);
	// An ads manager that loaded before the viewer arrived, waiting for them.
	const pendingManagerRef = useRef<any>(null);
	// isActive as a ref, so callbacks bound during the request still see the current value.
	const isActiveRef = useRef(false);
	// Whether the viewer ever actually reached this slot.
	const hasBeenActiveRef = useRef(false);
	const timeoutRef = useRef<any>(null);
	const sdkTimeoutRef = useRef<any>(null);
	const countdownRef = useRef<any>(null);
	const requestedRef = useRef(false);
	const finishedRef = useRef(false);
	// Both are read on the way out, where there is no state left to ask.
	const startedRef = useRef(false);
	const reportedNoFillRef = useRef(false);
	// No creative is coming: a plain card rather than a black slot.
	const [isEmpty, setIsEmpty] = useState(false);
	const [needsGesture, setNeedsGesture] = useState(false);
	// IMA's own UI has no volume control, so the mute toggle below is ours.
	const [isMuted, setIsMuted] = useState(muted ?? startVolume === 0);
	const [isAdPlaying, setIsAdPlaying] = useState(false);
	// IMA pauses the ad itself when a click-through opens the advertiser's page, and draws no control
	// to start it again - its UI is the click element, the countdown and the skip button, nothing
	// else. Without this the viewer comes back to a stopped ad and the only way on is to skip it.
	const [isAdPaused, setIsAdPaused] = useState(false);
	const isMutedRef = useRef(isMuted);
	// null: no countdown running. >0: seconds still to wait. 0: skippable now.
	const [skipCountdown, setSkipCountdown] = useState<number | null>(null);
	// A creative with a skipoffset gets IMA's skip button; we must not draw a second one.
	const [imaOwnsSkip, setImaOwnsSkip] = useState(false);

	const clearStartTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		if (sdkTimeoutRef.current) {
			clearTimeout(sdkTimeoutRef.current);
			sdkTimeoutRef.current = null;
		}
	}, []);

	const clearSkipCountdown = useCallback(() => {
		if (countdownRef.current) {
			clearInterval(countdownRef.current);
			countdownRef.current = null;
		}
	}, []);

	// Started on the ad's first frame, so a slow ad server does not eat the mandatory view time.
	const startSkipCountdown = useCallback(() => {
		clearSkipCountdown();
		setSkipCountdown(skipAfterSeconds);
		countdownRef.current = setInterval(() => {
			setSkipCountdown((remaining) => {
				if (remaining === null || remaining <= 1) {
					clearSkipCountdown();
					return 0;
				}
				return remaining - 1;
			});
		}, 1000);
	}, [clearSkipCountdown, skipAfterSeconds]);

	const teardown = useCallback(() => {
		clearStartTimeout();
		clearSkipCountdown();
		try {
			adsManagerRef.current?.destroy?.();
		} catch (e) {
			/* IMA throws if it was never initialised */
		}
		try {
			adsLoaderRef.current?.destroy?.();
		} catch (e) {
			/* as above */
		}
		adsManagerRef.current = null;
		adsLoaderRef.current = null;
	}, [clearStartTimeout, clearSkipCountdown]);

	const finish = useCallback(() => {
		if (finishedRef.current) {
			return;
		}
		finishedRef.current = true;
		teardown();
		onFinished();
	}, [onFinished, teardown]);

	// An unlaid-out slide reports 0x0, and IMA renders nothing into a zero-size slot.
	const getSlotSize = useCallback(() => {
		const width = containerRef.current?.clientWidth || window.innerWidth;
		const height = containerRef.current?.clientHeight || window.innerHeight;
		return { width, height };
	}, []);

	const applyMuted = useCallback(
		(next: boolean) => {
			isMutedRef.current = next;
			setIsMuted(next);
			try {
				adsManagerRef.current?.setVolume?.(next ? 0 : startVolume || 0.5);
			} catch (e) {
				/* manager already gone */
			}
		},
		[startVolume]
	);

	const toggleMuted = useCallback(() => {
		const next = !isMutedRef.current;
		applyMuted(next);
		onMutedChange?.(next);
	}, [applyMuted, onMutedChange]);

	// The feed's audio setting can also change from outside the slot.
	useEffect(() => {
		if (muted === undefined || muted === isMutedRef.current) {
			return;
		}
		applyMuted(muted);
	}, [muted, applyMuted]);

	// No ad, or one that failed before it played. Says so, offers a way out at once, and leaves by
	// itself - advancing instantly would read as the feed skipping a beat.
	const handleNoAd = useCallback(() => {
		if (finishedRef.current || reportedNoFillRef.current) {
			return;
		}
		clearStartTimeout();
		clearSkipCountdown();
		setIsEmpty(true);
		setSkipCountdown(0);
		// The feed's controls are handed straight back, and the feed - not this component - owns
		// what happens next. Swiping away unmounts the slot and takes any timer it was holding with
		// it, which left empty slots stuck in the list forever. See onNoFill in StoryFeed.
		reportedNoFillRef.current = true;
		onNoFill?.(isActiveRef.current);
	}, [clearStartTimeout, clearSkipCountdown, onNoFill]);

	// The ad's own transport. IMA reports PAUSED/RESUMED whoever caused them, so the button follows
	// a click-through pause as readily as one of ours.
	const toggleAdPlayback = useCallback(() => {
		const manager = adsManagerRef.current;
		if (!manager) {
			return;
		}
		try {
			isAdPaused ? manager.resume?.() : manager.pause?.();
		} catch (e) {
			/* the break ended under us */
		}
	}, [isAdPaused]);

	// Ends the break early. finish() is guarded, so it cannot race a natural completion.
	const skipAd = useCallback(() => {
		try {
			adsManagerRef.current?.stop?.();
		} catch (e) {
			/* nothing playing */
		}
		// Before finish(), which tears the slot down. A creative with no skipoffset defines no VAST
		// skip beacon, so this is the only record that the viewer left early.
		onSkipped?.();
		finish();
	}, [finish, onSkipped]);

	const startAds = useCallback(
		(adsManager: any) => {
			const google = (window as any).google;
			const { width, height } = getSlotSize();
			// Loaded is not playing: a creative can still fail to render. Armed here, where the ad
			// is asked to play, and cleared by STARTED.
			clearStartTimeout();
			timeoutRef.current = setTimeout(handleNoAd, AD_START_TIMEOUT_MS);
			const attempt = (level: number) => {
				adsManager.init(width, height, google.ima.ViewMode.NORMAL);
				// Follows the preset, like the content players.
				adsManager.setVolume(level);
				adsManager.start();
			};

			try {
				attempt(isMutedRef.current ? 0 : startVolume);
				setNeedsGesture(false);
			} catch (e) {
				// Autoplay can refuse audible playback: better a silent ad than none.
				try {
					isMutedRef.current = true;
					setIsMuted(true);
					attempt(0);
					setNeedsGesture(false);
				} catch (mutedError) {
					setNeedsGesture(true);
				}
			}
		},
		[clearStartTimeout, getSlotSize, handleNoAd]
	);

	const requestAds = useCallback((adsResponse?: string) => {
		const google = (window as any).google;
		if (!google?.ima || !containerRef.current || !videoRef.current) {
			handleNoAd();
			return;
		}

		if (google.ima.settings?.setDisableCustomPlaybackForIOS10Plus) {
			google.ima.settings.setDisableCustomPlaybackForIOS10Plus(true);
		}
		// Global to the SDK and read when the loader is built, so it has to be set before the two
		// lines below.
		if (locale && google.ima.settings?.setLocale) {
			google.ima.settings.setLocale(locale);
		}
		// IMA refuses VPAID creatives unless told otherwise. INSECURE matches what GoBrain uses.
		if (google.ima.settings?.setVpaidMode) {
			google.ima.settings.setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.INSECURE);
		}

		const displayContainer = new google.ima.AdDisplayContainer(containerRef.current, videoRef.current);
		// Must happen while a user gesture is still in scope - the swipe that brought this slide in.
		displayContainer.initialize();

		const adsLoader = new google.ima.AdsLoader(displayContainer);
		adsLoaderRef.current = adsLoader;

		// The loader errors when the tag returns no ad at all - the ordinary no-fill case.
		adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, handleNoAd, false);
		adsLoader.addEventListener(
			google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
			(event: any) => {
				// The server answered, which is all this timer asks. Running it to STARTED instead
				// killed slots that had an ad loaded and were only waiting to be scrolled to -
				// STARTED cannot fire until then, and clips run longer than the five seconds.
				clearStartTimeout();
				const renderingSettings = new google.ima.AdsRenderingSettings();
				renderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
				const adsManager = event.getAdsManager(videoRef.current, renderingSettings);
				adsManagerRef.current = adsManager;

				// A break that never put a frame on screen did not run. An ad tag answering with a
				// wrapper that holds no ad loads a manager quite happily and only finds it empty at
				// play time, then asks for the content back rather than erroring - which read here
				// as a completed break and had the host counting an ad nobody saw.
				const endOfBreak = () => (startedRef.current ? finish() : handleNoAd());

				// A creative that fails to render leaves the same empty slot as a no-fill.
				adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, handleNoAd);
				adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, endOfBreak);
				adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, endOfBreak);
				adsManager.addEventListener(google.ima.AdEvent.Type.PAUSED, () => setIsAdPaused(true));
				adsManager.addEventListener(google.ima.AdEvent.Type.RESUMED, () => setIsAdPaused(false));
				adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, (startedEvent: any) => {
					clearStartTimeout();
					startedRef.current = true;
					setIsAdPlaying(true);
					onStarted?.();

					// Skippability belongs to the creative's VAST skipoffset, not to the player.
					const ad = startedEvent?.getAd?.();
					const creativeIsSkippable = !!ad?.isSkippable?.();
					setImaOwnsSkip(creativeIsSkippable);
					if (creativeIsSkippable || skipAfterSeconds <= 0) {
						return;
					}
					startSkipCountdown();
				});

				// Loaded, but not necessarily wanted yet. Starting an ad on a slide nobody is looking
				// at would play it to an empty room and burn the impression.
				if (isActiveRef.current) {
					startAds(adsManager);
				} else {
					pendingManagerRef.current = adsManager;
				}
			},
			false
		);

		const adsRequest = new google.ima.AdsRequest();
		if (adsResponse) {
			// Already-fetched VAST, handed over as XML so IMA makes no request of its own.
			adsRequest.adsResponse = adsResponse;
		} else {
			// IMA resolves the tag itself, so a page-relative URL has to be absolute by now.
			adsRequest.adTagUrl = new URL(adTagUrl, window.location.href).href;
		}
		const { width, height } = getSlotSize();
		adsRequest.linearAdSlotWidth = width;
		adsRequest.linearAdSlotHeight = height;
		adsRequest.nonLinearAdSlotWidth = width;
		adsRequest.nonLinearAdSlotHeight = height;
		adsLoader.requestAds(adsRequest);
	}, [adTagUrl, clearStartTimeout, finish, handleNoAd, getSlotSize, locale, startAds, skipAfterSeconds, startSkipCountdown]);

	// Asked for on MOUNT, not on arrival. The feed keeps the neighbouring slide mounted
	// (STORY_SLIDE_WINDOW), so this slot exists - container and all - while the viewer is still
	// watching the clip before it. That head start is the whole point: by the time they scroll in we
	// already know whether there is an ad, so an empty slot can be stepped over instead of shown.
	// Nothing here STARTS an ad; that still waits for isActive, below.
	useEffect(() => {
		if (requestedRef.current || !adTagUrl) {
			return;
		}
		requestedRef.current = true;

		// Armed before the SDK request: a blocked or slow imasdk.googleapis.com must not leave the
		// viewer on a dead slot waiting for an onload that never arrives.
		timeoutRef.current = setTimeout(handleNoAd, AD_START_TIMEOUT_MS);
		const sdkTimer = setTimeout(() => {
			if (!(window as any).google?.ima) {
				handleNoAd();
			}
		}, SDK_READY_TIMEOUT_MS);
		sdkTimeoutRef.current = sdkTimer;

		// IMA fetches the tag from inside its own iframe, so a same-origin tag is cross-origin to
		// it - and on localhost, Private Network Access blocks it whatever CORS says. Fetching the
		// XML here and passing it as adsResponse sidesteps that. Remote tags take the normal path.
		let sameOrigin = false;
		try {
			sameOrigin = new URL(adTagUrl, window.location.href).origin === window.location.origin;
		} catch (e) {
			sameOrigin = false;
		}

		const onSdkReady = sameOrigin
			? () => {
					fetch(new URL(adTagUrl, window.location.href).href)
						.then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
						.then((xml) => requestAds(xml))
						.catch(handleNoAd);
			  }
			: () => requestAds();

		loadScript({ src: IMA_SDK_URL, id: 'qplayer-ima-sdk', onload: onSdkReady, onerror: handleNoAd });
	}, [adTagUrl, handleNoAd, requestAds]);

	// Mirrors isActive for the callbacks above, and starts an ad that loaded while the viewer was
	// still on the clip before this one.
	useEffect(() => {
		isActiveRef.current = isActive;
		if (!isActive || !pendingManagerRef.current) {
			return;
		}
		const waiting = pendingManagerRef.current;
		pendingManagerRef.current = null;
		startAds(waiting);
	}, [isActive, startAds]);

	// Swiping past mid-ad has to stop playback and release the SDK.
	//
	// Keyed on having BEEN here, not on having requested. The request now goes out while the slide
	// is still only a neighbour, so "requested but not active" is the ordinary state of a slot
	// waiting to be reached - and treating that as a swipe-past marked it finished the moment it
	// mounted, which in turn made handleNoAd return early and left the slot locked for good with
	// nothing ever reported.
	useEffect(() => {
		if (isActive) {
			hasBeenActiveRef.current = true;
			return;
		}
		if (hasBeenActiveRef.current && !finishedRef.current) {
			finishedRef.current = true;
			// Leaving a slot that never played anything. finishedRef makes handleNoAd return early
			// from here on, so this is the last chance to tell the feed the slot is empty.
			if (!startedRef.current && !reportedNoFillRef.current) {
				reportedNoFillRef.current = true;
				onNoFill?.(false);
			}
			teardown();
		}
	}, [isActive, onNoFill, teardown]);

	useEffect(() => teardown, [teardown]);

	return (
		<div
			className={`qplayer-story-ad${isEmpty ? ' qplayer-story-ad--empty' : ''}`}
			role="group"
			aria-label={(isEmpty ? noAdLabel : label) ?? (isEmpty ? 'No advertisement' : 'Advertisement')}
		>
			{/* IMA needs a content video element to hang playback off, even with no content. */}
			<video ref={videoRef} className="qplayer-story-ad__video" muted playsInline preload="none" />
			<div ref={containerRef} className="qplayer-story-ad__container" />
			{isAdPlaying && !needsGesture && (
				<button
					type="button"
					className="qplayer-story-ad__playback"
					onClick={toggleAdPlayback}
					aria-label={(isAdPaused ? playLabel : pauseLabel) ?? (isAdPaused ? 'Play' : 'Pause')}
				>
					{isAdPaused ? (
						<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
							<path d="M8 5v14l11-7L8 5Z" />
						</svg>
					) : (
						<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
							<path d="M7 5h3.2v14H7V5Zm6.8 0H17v14h-3.2V5Z" />
						</svg>
					)}
				</button>
			)}
			{isAdPlaying && !needsGesture && (
				<button
					type="button"
					className="qplayer-story-ad__mute"
					onClick={toggleMuted}
					aria-label={(isMuted ? unmuteLabel : muteLabel) ?? (isMuted ? 'Unmute' : 'Mute')}
					aria-pressed={isMuted}
				>
					{isMuted ? (
						<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
							<path d="M3.6 3.6 2.2 5l4.3 4.3H3v5.4h4l5 5v-6.7l4.2 4.2a6.3 6.3 0 0 1-1.7.9v2.1a8.4 8.4 0 0 0 3.2-1.6l2.1 2.1 1.4-1.4L3.6 3.6Zm8.4.9-2.6 2.6L12 9.7V4.5Zm5.6 7.5a5 5 0 0 0-1-3l1.5-1.5a7.2 7.2 0 0 1 0 9l-1.5-1.5a5 5 0 0 0 1-3Z" />
						</svg>
					) : (
						<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
							<path d="M3 9.3v5.4h4l5 5V4.3l-5 5H3Zm11.5-.6a5 5 0 0 1 0 6.6l1.5 1.5a7.2 7.2 0 0 0 0-9.6l-1.5 1.5Zm2.6-2.6a8.7 8.7 0 0 1 0 11.8l1.5 1.5a10.9 10.9 0 0 0 0-14.8l-1.5 1.5Z" />
						</svg>
					)}
				</button>
			)}
			{/* The feed's step controls are dead during an ad, so this is the only way out. An empty
			    slot has nothing to watch, so it is skippable at once. */}
			{/* An empty slot gets no control. It is not something the viewer has to get out of - the
			    feed scrolls off it on its own the moment it knows there is nothing there - so a
			    button offering to leave reads as a thing gone wrong rather than a way forward. Only
			    a running ad, which does hold them, is worth a way past. */}
			{isAdPlaying && !imaOwnsSkip && skipCountdown !== null && (
				skipCountdown > 0 ? (
					<span className="qplayer-story-ad__skip qplayer-story-ad__skip--waiting" aria-live="polite">
						{(skipCountdownLabel ?? 'Skip in {{seconds}}s').replace('{{seconds}}', String(skipCountdown))}
					</span>
				) : (
					<button type="button" className="qplayer-story-ad__skip" onClick={skipAd}>
						{skipLabel ?? 'Skip ad'}
						<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
							<path d="M4 5l9 7-9 7V5Zm10 0h2.4v14H14V5Zm4 0h2v14h-2V5Z" />
						</svg>
					</button>
				)
			)}
			{needsGesture && (
				<button
					type="button"
					className="qplayer-story-ad__gesture"
					onClick={() => adsManagerRef.current && startAds(adsManagerRef.current)}
					aria-label={label ?? 'Advertisement'}
				/>
			)}
		</div>
	);
};

export default StoryAdSlide;
